/**
 * Customer settings domain types + defaults.
 *
 * Two sub-sections — "Customer Portal" (feature toggles) and "Customer Tags"
 * (segmentation rules). No live endpoint yet, so the slice is seeded from
 * `defaultCustomerSettings()` and persisted in-memory. When the events API
 * publishes `/events/api/v1/settings/customer/`, swap the seed for an SSR
 * fetcher — the slice + type shape stay the same.
 */

export interface CustomerPortalSettings {
  /** Master switch — gates every other portal capability below. */
  enablePortal: boolean;
  sendLoginLinkAtConfirmation: boolean;
  allowInvoicePayment: boolean;
  allowChangeRequest: boolean;
  allowCancellationRequest: boolean;
}

export interface CustomerTagRules {
  /** Auto-tag "Regular" after this many repeat bookings. */
  regularAfterBookings: number;
  /** Auto-tag "VIP" when a single event spend reaches this amount. */
  vipEventSpend: number;
  /** Auto-tag "High Value" when lifetime spend reaches this amount. */
  highValueLifetimeSpend: number;
}

export interface CustomerSettings {
  portal: CustomerPortalSettings;
  tags: CustomerTagRules;
}

/** The system-managed segmentation tags shown as locked chips. */
export const CUSTOMER_TAGS = [
  { label: "VIP", color: "purple" as const },
  { label: "Regular", color: "info" as const },
  { label: "High Value", color: "error" as const },
];

/** The portal toggle rows, in display order. The first row is the master. */
export const PORTAL_TOGGLES: {
  key: keyof CustomerPortalSettings;
  title: string;
  subtitle: string;
}[] = [
  {
    key: "enablePortal",
    title: "Enable customer portal",
    subtitle:
      "Customers can log in to view bookings, invoices, contracts, and communicate with staff",
  },
  {
    key: "sendLoginLinkAtConfirmation",
    title: "Send portal login link at booking confirmation",
    subtitle:
      "Customer receives magic link email to set password when booking is confirmed",
  },
  {
    key: "allowInvoicePayment",
    title: "Allow invoice payment via portal",
    subtitle: "Stripe-powered card payments for deposits and balances",
  },
  {
    key: "allowChangeRequest",
    title: "Allow change request submission",
    subtitle:
      "Customers can submit modification requests — staff reviews before applying",
  },
  {
    key: "allowCancellationRequest",
    title: "Allow cancellation request via portal",
    subtitle: "Goes to staff for review — never auto-confirmed",
  },
];

export function defaultCustomerSettings(): CustomerSettings {
  return {
    portal: {
      enablePortal: true,
      sendLoginLinkAtConfirmation: true,
      allowInvoicePayment: true,
      allowChangeRequest: true,
      allowCancellationRequest: false,
    },
    tags: {
      regularAfterBookings: 2,
      vipEventSpend: 25000,
      highValueLifetimeSpend: 10000,
    },
  };
}
